import React, { useEffect, useState, useCallback } from 'react';
import { Ticket, Check, Copy, Loader2, RefreshCw, ShoppingCart, Euro, ChevronDown, ChevronRight, ArrowLeft, Plus, Minus, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './dialog';
import { Button } from './button';
import { Input } from './input';
import { PRICES } from '../config/prices';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// Custom-amount vouchers must be at least €5 — smaller values waste the
// payment-processor floor and the price tiers we sell already cover lower
// amounts as presets.
const MIN_CUSTOM_AMOUNT_CENTS = 500;

const formatVoucherAmount = (v) => {
  if (v.discountType === 'amount') {
    return `€${((v.discountAmountCents || 0) / 100).toFixed(2)} off`;
  }
  const pct = (v.discountPercent || 0) * 100;
  return `${pct.toFixed(0)}% off`;
};

const STATUS_STYLES = {
  active: 'bg-gray-200 text-gray-900',
  used: 'bg-gray-100 text-gray-500',
  expired: 'bg-gray-100 text-gray-500',
};

const formatDate = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString();
  } catch (_e) {
    return null;
  }
};

const sourceLabel = (v) => {
  if (v.source === 'remainder') return 'Leftover from a previous purchase';
  if (v.source === 'purchase') {
    const d = formatDate(v.createdAt);
    return d ? `Bought ${d}` : 'Bought';
  }
  return 'Voucher';
};

// Preset cards describe the unlock in plain language — easier to grok than
// "M" / "L" since vouchers can be gifted to people who don't know our sizing.
const PRESET_AMOUNTS_CENTS = [
  { label: 'Size M Image', sub: 'eg. 40x30cm for Format 4:3', cents: Math.round((PRICES.M || 2.49) * 100) },
  { label: 'Size L Image', sub: 'eg. 80x60cm for Format 4:3', cents: Math.round((PRICES.L || 4.49) * 100) },
];

const VouchersDialog = ({ open, onOpenChange }) => {
  const [vouchers, setVouchers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);
  // Explicit mode toggle so the custom card visually selects the moment it
  // is clicked, without requiring the user to type a number first.
  const [mode, setMode] = useState('preset'); // 'preset' | 'custom'
  const [selectedAmountCents, setSelectedAmountCents] = useState(PRESET_AMOUNTS_CENTS[0].cents);
  const [customAmountInput, setCustomAmountInput] = useState('');
  const [buyLoading, setBuyLoading] = useState(false);
  const [buyError, setBuyError] = useState(null);
  const [inactiveOpen, setInactiveOpen] = useState(false);
  // 'list' shows owned vouchers + a "Buy a voucher" CTA. 'buy' swaps
  // the panel for the preset / browse / custom-amount cards. 'browse'
  // lists every print product × size with its live Prodigi price so
  // the user can pick a voucher amount that exactly covers a print.
  // Reset to 'list' whenever the dialog re-opens so a previous buy
  // attempt doesn't linger after the user closed and re-opened it.
  const [view, setView] = useState('list');
  const [browseItems, setBrowseItems] = useState(null);
  const [browseLoading, setBrowseLoading] = useState(false);
  const [browseError, setBrowseError] = useState(null);

  const fetchVouchers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/user/vouchers`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to load vouchers');
      const data = await r.json();
      setVouchers(data.vouchers || []);
    } catch (e) {
      setError(e.message || 'Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchVouchers();
      setView('list');
      setBuyError(null);
    }
  }, [open, fetchVouchers]);

  const fetchBrowseItems = useCallback(async () => {
    setBrowseLoading(true);
    setBrowseError(null);
    try {
      const r = await fetch(`${API_URL}/prodigi/catalogue`);
      if (!r.ok) throw new Error('Failed to load catalogue');
      const data = await r.json();
      const products = data.products || [];
      const token = localStorage.getItem('token');
      // Fetch one Prodigi quote per (product, tier). Backend caches
      // quotes for 24h so cold load is the only slow path; later
      // browses hit the cache and return instantly.
      const items = [];
      for (const product of products) {
        const sizes = product.sizes || [];
        const quotes = await Promise.all(sizes.map(async (s) => {
          try {
            const params = new URLSearchParams({ product_id: product.id, tier: s.tier, country: 'DE' });
            const qr = await fetch(`${API_URL}/prodigi/quote?${params}`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!qr.ok) return null;
            return qr.json();
          } catch (_e) { return null; }
        }));
        sizes.forEach((s, i) => {
          if (!quotes[i] || quotes[i].item_price == null) return;
          items.push({
            productId: product.id,
            productLabel: product.label,
            productSubtitle: product.subtitle,
            tier: s.tier,
            cm: s.cm,
            priceCents: Math.round((quotes[i].item_price || 0) * 100),
          });
        });
      }
      items.sort((a, b) => a.priceCents - b.priceCents);
      setBrowseItems(items);
    } catch (e) {
      setBrowseError(e.message || 'Failed to load prices');
    } finally {
      setBrowseLoading(false);
    }
  }, []);

  useEffect(() => {
    if (view === 'browse' && browseItems === null && !browseLoading) {
      fetchBrowseItems();
    }
  }, [view, browseItems, browseLoading, fetchBrowseItems]);

  const selectBrowseItem = (item) => {
    setMode('custom');
    // Use the print's exact price (cents-precision) so the voucher
    // matches the print at checkout — e.g. €15.99, not €16.
    setCustomAmountInput((item.priceCents / 100).toFixed(2));
    setView('buy');
  };

  const handleBuy = async () => {
    setBuyError(null);
    let amount = selectedAmountCents;
    const isCustom = mode === 'custom';
    if (isCustom) {
      const parsed = Math.round(parseFloat(customAmountInput) * 100);
      amount = Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    }
    if (!amount || amount <= 0) {
      setBuyError('Pick a preset or enter a custom amount.');
      return;
    }
    if (isCustom && amount < MIN_CUSTOM_AMOUNT_CENTS) {
      setBuyError('Custom amounts start at €5.00.');
      return;
    }
    const token = localStorage.getItem('token');
    if (!token) {
      setBuyError('Please sign in first.');
      return;
    }
    setBuyLoading(true);
    try {
      const r = await fetch(`${API_URL}/buy-voucher`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ amount_cents: amount }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to start checkout');
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('Checkout URL missing');
    } catch (e) {
      setBuyError(e.message || 'Failed to start checkout');
      setBuyLoading(false);
    }
  };

  const handleCopy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCode(code);
      setTimeout(() => setCopiedCode((c) => (c === code ? null : c)), 1500);
    } catch (_e) {
      // Clipboard API may be blocked in non-secure contexts; the code is
      // still selectable in the dialog.
    }
  };

  const renderVoucherRow = (v) => (
    <div
      key={v.id || v.code}
      className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2"
    >
      <div className="flex items-start justify-between gap-2">
        <code className="font-mono text-sm font-semibold break-all">{v.code}</code>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
            STATUS_STYLES[v.status] || STATUS_STYLES.expired
          }`}
        >
          {v.status === 'active' ? 'Valid' : v.status === 'used' ? 'Already used' : 'Expired'}
        </span>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-700">
        <div>
          <p className="font-medium text-gray-900">{formatVoucherAmount(v)}</p>
          <p className="text-gray-500">
            {sourceLabel(v)}
            {v.expiryDate && (
              <> · expires {new Date(v.expiryDate).toLocaleDateString()}</>
            )}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleCopy(v.code)}
          disabled={v.status !== 'active'}
          className={`flex-shrink-0 transition-colors ${
            copiedCode === v.code ? 'border-gray-900 text-gray-900' : ''
          }`}
        >
          {copiedCode === v.code ? (
            <>
              <Check className="h-4 w-4 mr-1" /> Copied
            </>
          ) : (
            <>
              <Copy className="h-4 w-4 mr-1" /> Copy
            </>
          )}
        </Button>
      </div>
    </div>
  );

  const activeVouchers = (vouchers || []).filter((v) => v.status === 'active');
  const inactiveVouchers = (vouchers || []).filter((v) => v.status !== 'active');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Match the map purchase popup's responsive sizing:
          - Mobile: 90vw with a gutter from the viewport edges, corners
            rounded at every size.
          - sm+: dynamic up to max-w-lg. */}
      <DialogContent className="bg-white rounded-2xl p-0 overflow-hidden w-[90vw] sm:w-full max-w-lg">
        <div className="bg-gradient-to-br from-gray-900 to-gray-700 text-white px-6 py-5 flex items-center gap-3">
          {view !== 'list' ? (
            <button
              type="button"
              onClick={() => {
                if (view === 'browse') setView('buy');
                else { setView('list'); setBuyError(null); }
              }}
              className="h-9 w-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center"
              title="Back"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <div className="h-10 w-10 rounded-full bg-white/15 flex items-center justify-center">
              <Ticket className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1">
            <DialogTitle className="text-lg font-semibold">
              {view === 'buy' ? 'Buy a voucher'
                : view === 'browse' ? 'Browse print prices'
                : 'Your vouchers'}
            </DialogTitle>
            <p className="text-xs text-gray-300 mt-0.5">
              {view === 'buy'
                ? 'Pay once, redeem anytime. Codes can be shared with friends.'
                : view === 'browse'
                  ? 'Pick a print — its price becomes the voucher amount.'
                  : 'Bought vouchers and leftovers from previous purchases. Codes can be shared.'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-8 w-8 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center flex-shrink-0"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto space-y-5">
          {view === 'browse' ? (
            <section className="space-y-2">
              {browseLoading && (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading print prices…
                </div>
              )}
              {browseError && (
                <div className="text-sm text-red-500 text-center py-4">{browseError}</div>
              )}
              {!browseLoading && !browseError && browseItems && browseItems.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-8">
                  No print prices available right now.
                </div>
              )}
              {!browseLoading && browseItems && browseItems.length > 0 && (
                <div className="space-y-1.5">
                  {browseItems.map((item) => (
                    <button
                      key={`${item.productId}-${item.tier}`}
                      type="button"
                      onClick={() => selectBrowseItem(item)}
                      className="w-full p-3 rounded-xl border border-gray-200 hover:bg-gray-50 text-sm flex items-center justify-between gap-3 transition-colors"
                    >
                      <div className="text-left min-w-0">
                        <div className="font-medium truncate">{item.productLabel}</div>
                        <div className="text-[11px] text-gray-500 truncate">
                          Size {item.tier} · {item.cm} cm
                        </div>
                      </div>
                      <div className="font-semibold flex-shrink-0">€{(item.priceCents / 100).toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              )}
            </section>
          ) : view === 'buy' ? (
            <section className="space-y-3">
              {/* 2x2 grid: M, L, Browse prints, Custom. Each card has
                  the price/headline centered on the first line, then
                  the title and a one-line description. Selected cards
                  flip to a black-on-white-pills look so the controls
                  inside the Custom card stay legible. */}
              <div className="grid grid-cols-2 gap-2">
                {PRESET_AMOUNTS_CENTS.map((p) => {
                  const isSelected = mode === 'preset' && selectedAmountCents === p.cents;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => { setMode('preset'); setSelectedAmountCents(p.cents); }}
                      className={`p-3 rounded-xl border text-sm transition-all text-center ${
                        isSelected
                          ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="text-base font-semibold">€{(p.cents / 100).toFixed(2)}</div>
                      <div className="mt-1 text-sm font-medium">{p.label}</div>
                      <div className={`text-[11px] mt-0.5 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>{p.sub}</div>
                    </button>
                  );
                })}

                {/* Browse prints — opens the sub-view that lists every
                    product × size with the live Prodigi price. Not a
                    selectable preset itself; clicking inside the
                    browse view is what actually sets the amount. */}
                <button
                  type="button"
                  onClick={() => setView('browse')}
                  className="p-3 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-sm text-center transition-all"
                >
                  <div className="text-base font-semibold flex items-center justify-center gap-1">
                    <Search className="h-4 w-4" /> Browse
                  </div>
                  <div className="mt-1 text-sm font-medium">Browse prints</div>
                  <div className="text-[11px] text-gray-500 mt-0.5">See all sizes and prices</div>
                </button>

                {(() => {
                  // Custom-amount card. When `mode === 'custom'` the card
                  // flips to a black background; the input and ± buttons
                  // stay white pills so they remain readable on the dark
                  // background.
                  const isSelected = mode === 'custom';
                  const adjustCustom = (delta) => {
                    setMode('custom');
                    const parsed = parseFloat(customAmountInput);
                    const base = Number.isFinite(parsed) && parsed > 0 ? parsed : 5;
                    const next = Math.max(5, Math.round(base + delta));
                    setCustomAmountInput(String(next));
                  };
                  return (
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={() => setMode('custom')}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('custom'); } }}
                      className={`p-3 rounded-xl border text-sm transition-all cursor-pointer text-center ${
                        isSelected
                          ? 'border-gray-900 bg-gray-900 text-white shadow-sm'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className="flex items-center justify-center gap-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          type="button"
                          onClick={() => adjustCustom(-5)}
                          title="-€5"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-white text-gray-900 shadow-sm hover:bg-gray-100 transition-colors"
                        >
                          <Minus className="h-3.5 w-3.5" />
                        </button>
                        <div className="flex items-center bg-white text-gray-900 rounded-xl px-2 h-8">
                          <Euro className="h-3.5 w-3.5 text-gray-400 mr-1" />
                          <Input
                            type="number"
                            min="5"
                            step="0.01"
                            placeholder="5"
                            value={customAmountInput}
                            onChange={(e) => setCustomAmountInput(e.target.value)}
                            onFocus={() => setMode('custom')}
                            className="w-16 border-0 p-0 h-auto bg-transparent text-sm font-semibold text-center text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:ring-offset-0 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => adjustCustom(5)}
                          title="+€5"
                          className="h-7 w-7 rounded-full flex items-center justify-center bg-white text-gray-900 shadow-sm hover:bg-gray-100 transition-colors"
                        >
                          <Plus className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <div className="mt-1 text-sm font-medium">Custom amount</div>
                      <div className={`text-[11px] mt-0.5 ${isSelected ? 'text-gray-300' : 'text-gray-500'}`}>Min €5</div>
                    </div>
                  );
                })()}
              </div>
              <div className="flex justify-center pt-1">
                <Button
                  onClick={handleBuy}
                  disabled={buyLoading}
                  className="bg-gray-900 hover:bg-gray-700 text-white border-0 shadow-md text-sm font-semibold"
                >
                  {buyLoading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Redirecting…</>
                  ) : (
                    <><ShoppingCart className="h-4 w-4 mr-2" /> Buy voucher</>
                  )}
                </Button>
              </div>
              {buyError && <p className="text-xs text-red-500 text-center">{buyError}</p>}
              <p className="text-[11px] text-gray-500 text-center">
                Codes can be shared — give them to friends or keep for yourself.
              </p>
            </section>
          ) : (
            <>
              <Button
                onClick={() => { setBuyError(null); setView('buy'); }}
                className="w-full bg-gray-900 hover:bg-gray-700 text-white border-0 shadow-md text-sm font-semibold"
              >
                <ShoppingCart className="h-4 w-4 mr-2" /> Buy a voucher
              </Button>

              {loading && vouchers === null && (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading vouchers…
                </div>
              )}
              {error && (
                <div className="text-sm text-red-500 text-center py-4">{error}</div>
              )}
              {vouchers && vouchers.length === 0 && !loading && (
                <div className="text-sm text-gray-500 text-center py-8">
                  You don't have any vouchers yet. Leftover credit from a fixed-amount voucher
                  redemption will show up here.
                </div>
              )}
              {activeVouchers.map(renderVoucherRow)}
              {inactiveVouchers.length > 0 && (
                <div className="pt-1">
                  <button
                    type="button"
                    onClick={() => setInactiveOpen((x) => !x)}
                    className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                  >
                    {inactiveOpen
                      ? <ChevronDown className="h-3.5 w-3.5" />
                      : <ChevronRight className="h-3.5 w-3.5" />
                    }
                    Used &amp; expired ({inactiveVouchers.length})
                  </button>
                  {inactiveOpen && (
                    <div className="mt-2 space-y-3">
                      {inactiveVouchers.map(renderVoucherRow)}
                    </div>
                  )}
                </div>
              )}
              {/* Manual refresh — same `fetchVouchers` call the auto-open
                  effect runs, now reachable as an explicit "Sync" button
                  underneath the list rather than the bare icon that used
                  to sit in the header. */}
              <div className="flex justify-center pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchVouchers}
                  disabled={loading}
                  className="rounded-2xl"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Sync
                </Button>
              </div>
            </>
          )}
        </div>

      </DialogContent>
    </Dialog>
  );
};

export default VouchersDialog;
